<script>
/**
 * @component PrescriptionHistory
 * @description Historial de solicitudes de recetas procesadas (aprobadas, rechazadas, canceladas).
 * @example <PrescriptionHistory />
 */
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import medicationRequestService from "@/services/medicationRequest";
import helperService from "@/services/helper";

export default {
  name: "PrescriptionHistory",

  components: { BaseSubNav, Spinner },

  data() {
    return {
      isLoading: false,
      medicationRequests: [],
      searchInput: "",
    };
  },

  computed: {
    /**
     * @description Solicitudes filtradas por búsqueda de texto libre
     * @returns {Array}
     */
    filteredMedicationRequests() {
      if (!this.searchInput) return this.medicationRequests;
      const query = this.searchInput.toLowerCase();
      return this.medicationRequests.filter((r) => {
        const med =
          r.medication?.name ||
          r.publicMedication?.name ||
          r.publicMedication?.brandName ||
          "";
        return (
          r.patient?.firstName?.toLowerCase().includes(query) ||
          r.patient?.lastName?.toLowerCase().includes(query) ||
          med.toLowerCase().includes(query) ||
          this.getDiagnosisName(r).toLowerCase().includes(query)
        );
      });
    },
  },

  watch: {
    "$route.name": {
      handler() {
        this.getData();
      },
      immediate: true,
    },
  },

  methods: {
    /**
     * @description Carga el historial de solicitudes procesadas
     */
    async getData() {
      this.isLoading = true;
      try {
        const res = await medicationRequestService.get({
          excludePending: true,
        });
        this.medicationRequests = res.docs || res || [];
      } catch (error) {
        console.error("Error loading history:", error);
        this.$message.error("Error al cargar el historial");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Nombre completo del paciente
     * @param {Object} patient
     * @returns {string}
     */
    getFullName(patient) {
      return helperService.getFullName(patient);
    },

    /**
     * @description Nombre del medicamento de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getMedicationName(request) {
      return (
        request.medication?.name ||
        request.publicMedication?.name ||
        request.publicMedication?.brandName ||
        "Sin medicación"
      );
    },

    /**
     * @description Diagnóstico de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getDiagnosisName(request) {
      if (typeof request.diagnosis === "string") return request.diagnosis;
      if (request.diagnosis?.name) return request.diagnosis.name;
      if (typeof request.publicDiagnosis === "string")
        return request.publicDiagnosis;
      if (request.publicDiagnosis?.name) return request.publicDiagnosis.name;
      return "Sin diagnóstico";
    },

    /**
     * @description Texto legible del estado
     * @param {string} status
     * @returns {string}
     */
    getStatusText(status) {
      return (
        {
          pending: "Pendiente",
          approved: "Aprobada",
          rejected: "Rechazada",
          cancelled: "Cancelada",
        }[status] || status
      );
    },

    /**
     * @description Clase CSS del badge según estado
     * @param {string} status
     * @returns {string}
     */
    getStatusClass(status) {
      return (
        {
          pending: "pq-badge--pending",
          approved: "pq-badge--approved",
          rejected: "pq-badge--rejected",
          cancelled: "pq-badge--cancelled",
        }[status] || ""
      );
    },

    /**
     * @description Navega al dashboard del paciente
     * @param {Object|string} patient
     */
    goToPatientDashboard(patient) {
      const id = typeof patient === "string" ? patient : patient._id;
      this.$router.push({ name: "patient-dashboard", params: { id } });
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(navigationType="prescription" :showNavigation="true")
    template(#actions)
      .pq-search
        micon(name="search")
        input.pq-search__input(
          v-model="searchInput"
          placeholder="Buscar por paciente, medicación o diagnóstico..."
        )

  .pq-card
    //- Loading
    .pq-loading(v-if="isLoading")
      spinner

    //- Empty state
    .pq-empty(v-else-if="!filteredMedicationRequests.length")
      micon(name="history")
      p.pq-empty__title
        | {{ searchInput ? 'Sin resultados para "' + searchInput + '"' : 'No hay solicitudes en el historial' }}
      p.pq-empty__sub(v-if="!searchInput") Las solicitudes aprobadas y rechazadas aparecerán aquí

    //- Tabla
    table.pq-table(v-else)
      thead
        tr
          th Paciente
          th Diagnóstico
          th Medicación
          th Estado
          th Fecha de solicitud
      tbody
        tr(
          v-for="request in filteredMedicationRequests"
          :key="request._id"
          @click="goToPatientDashboard(request.patient)"
        )
          td
            .pq-patient {{ getFullName(request.patient) }}
          td
            span.pq-diag {{ getDiagnosisName(request) }}
          td
            .pq-med {{ getMedicationName(request) }}
          td
            span.pq-badge(:class="getStatusClass(request.status)") {{ getStatusText(request.status) }}
          td
            span.pq-date {{ request.createdAt | formatDateAndTime }}
</template>

<style lang="scss" scoped>
// ── Search ────────────────────────────────────────────
.pq-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 12px;
  width: 320px;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: #1e3a5f;
    background: #fff;
  }

  .micon {
    width: 16px;
    height: 16px;
    fill: #94a3b8;
    flex-shrink: 0;
  }

  &__input {
    border: none;
    background: none;
    outline: none;
    font-size: 13px;
    color: #1e293b;
    width: 100%;
    font-family: inherit;

    &::placeholder {
      color: #94a3b8;
    }
  }
}

// ── Card ──────────────────────────────────────────────
.pq-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  margin-top: 16px;
}

// ── Loading / empty ────────────────────────────────────
.pq-loading {
  padding: 48px;
  text-align: center;
}

.pq-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;

  .micon {
    width: 40px;
    height: 40px;
    fill: #e2e8f0;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
  }

  &__sub {
    font-size: 13px;
    color: #94a3b8;
  }
}

// ── Tabla ─────────────────────────────────────────────
.pq-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #f1f5f9;
    white-space: nowrap;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f8fafc;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.1s;

    &:last-child td {
      border-bottom: none;
    }
    &:hover td {
      background: #f8fafc;
    }
  }
}

.pq-patient {
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
}

.pq-diag {
  font-size: 13px;
  color: #475569;
}

.pq-med {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
}

.pq-date {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

// Badges de estado
.pq-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &--pending {
    background: #fff7ed;
    color: #ea580c;
    &::before {
      background: #ea580c;
    }
  }
  &--approved {
    background: #f0fdf4;
    color: #16a34a;
    &::before {
      background: #16a34a;
    }
  }
  &--rejected {
    background: #fef2f2;
    color: #dc2626;
    &::before {
      background: #dc2626;
    }
  }
  &--cancelled {
    background: #f8fafc;
    color: #94a3b8;
    &::before {
      background: #94a3b8;
    }
  }
}

@media (max-width: 767px) {
  .pq-search {
    width: 200px;
  }
  .pq-table {
    th:nth-child(2),
    td:nth-child(2) {
      display: none;
    }
  }
}
</style>
